import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Row, Col, Form, Button, Badge, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faExternalLink, faFloppyDisk, faBoxOpen } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";

import PageHeader from "../../Components/Common/PageHeader";
import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";
import "./style.scss";

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);

  const [form, setForm] = useState({
    title: "",
    description: "",
    shortDescription: "",
    sku: "",
    price: "",
    salePrice: "",
    currency: "USD",
    tagsCsv: "",
    materialsCsv: "",
    stock: "",
    stockStatus: "instock",
    status: "ready",
    category: "",
  });

  useEffect(() => {
    Utils.setPageTitle("Product detail");
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const res = await Utils._apiRequest({
          path: Enums.endpoints.products.DETAIL,
          pathParams: { id },
          type: "GET",
        });
        if (res.errors) throw new Error(res.errors);
        const p = res.data;
        setProduct(p);
        setForm({
          title: p.title || "",
          description: p.description || "",
          shortDescription: p.shortDescription || "",
          sku: p.sku || "",
          price: p.price ?? "",
          salePrice: p.salePrice ?? "",
          currency: p.currency || "USD",
          tagsCsv: (p.tags || []).join(", "),
          materialsCsv: (p.materials || []).join(", "),
          stock: p.stock ?? "",
          stockStatus: p.stockStatus || "instock",
          status: p.status || "ready",
          category: p.category?._id || p.category || "",
        });

        // Load categories for the same workspace
        const catRes = await Utils._apiRequest({
          path: Enums.endpoints.products.CATEGORIES,
          type: "GET",
          query: { workspace: p.workspace },
        });
        setCategories(catRes.data || []);
      } catch (err) {
        toast.error(err.message || "Không tải được sản phẩm");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const handleChange = (field) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
  };

  const csvToArr = (s) =>
    (s || "")
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        description: form.description,
        shortDescription: form.shortDescription,
        sku: form.sku.trim(),
        price: form.price === "" ? null : Number(form.price),
        salePrice: form.salePrice === "" ? null : Number(form.salePrice),
        currency: form.currency.trim() || "USD",
        tags: csvToArr(form.tagsCsv),
        materials: csvToArr(form.materialsCsv),
        stock: form.stock === "" ? 0 : Number(form.stock),
        stockStatus: form.stockStatus,
        status: form.status,
        category: form.category || null,
      };
      const res = await Utils._apiRequest({
        path: Enums.endpoints.products.DETAIL,
        pathParams: { id },
        type: "PUT",
        payload,
      });
      if (res.errors) throw new Error(res.errors);
      toast.success("Đã lưu thay đổi");
      setProduct(res.data);
    } catch (err) {
      toast.error(err.message || "Lỗi khi lưu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <main className="page-main">
        <div className="main-container container text-center py-5">
          <Spinner animation="border" />
        </div>
      </main>
    );
  }

  if (!product) {
    return (
      <main className="page-main">
        <div className="main-container container text-center py-5">
          Không tìm thấy sản phẩm.{" "}
          <Link to="/products">Quay lại danh sách</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page-main">
      <div className="main-container container">
        <PageHeader
          title={product.title}
          description={
            <span>
              <Badge bg="info" className="me-2 text-uppercase">
                {product.source}
              </Badge>
              {product.sourceShop && (
                <span className="text-muted">Shop: {product.sourceShop}</span>
              )}
            </span>
          }
          icon={faBoxOpen}
        >
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate("/products")}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Quay lại
            </Button>
            {product.sourceUrl && (
              <a
                href={product.sourceUrl}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-primary btn-sm"
              >
                <FontAwesomeIcon icon={faExternalLink} className="me-1" />
                Mở trang gốc
              </a>
            )}
          </div>
        </PageHeader>

        <Form onSubmit={handleSave}>
          <Row className="g-3">
            <Col lg={8}>
              <div className="products-detail__section">
                <h6 className="mb-3">Thông tin chính</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    value={form.title}
                    onChange={handleChange("title")}
                    required
                  />
                </Form.Group>

                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>SKU</Form.Label>
                      <Form.Control
                        value={form.sku}
                        onChange={handleChange("sku")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Category</Form.Label>
                      <Form.Select
                        value={form.category}
                        onChange={handleChange("category")}
                      >
                        <option value="">-- Không có --</option>
                        {categories.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Short description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={form.shortDescription}
                    onChange={handleChange("shortDescription")}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={8}
                    value={form.description}
                    onChange={handleChange("description")}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Tags (phân cách bởi dấu phẩy)</Form.Label>
                  <Form.Control
                    value={form.tagsCsv}
                    onChange={handleChange("tagsCsv")}
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Materials (phân cách bởi dấu phẩy)</Form.Label>
                  <Form.Control
                    value={form.materialsCsv}
                    onChange={handleChange("materialsCsv")}
                  />
                </Form.Group>
              </div>

              {product.images?.length > 0 && (
                <div className="products-detail__section">
                  <h6 className="mb-3">Gallery ({product.images.length} ảnh)</h6>
                  <div className="products-detail__gallery">
                    {product.images.map((img, idx) => (
                      <img key={idx} src={img.url} alt={img.alt || ""} />
                    ))}
                  </div>
                </div>
              )}

              {product.variants?.length > 0 && (
                <div className="products-detail__section">
                  <h6 className="mb-3">Variants ({product.variants.length})</h6>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Options</th>
                          <th>Price</th>
                          <th>Stock</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.variants.map((v, i) => (
                          <tr key={i}>
                            <td>{v.sku || "--"}</td>
                            <td>
                              {(v.options || [])
                                .map((o) => `${o.name}: ${o.value}`)
                                .join(", ")}
                            </td>
                            <td>{v.price ?? "--"}</td>
                            <td>{v.stock ?? "--"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </Col>

            <Col lg={4}>
              <div className="products-detail__section">
                <h6 className="mb-3">Giá & Stock</h6>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Giá</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={form.price}
                        onChange={handleChange("price")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Sale price</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        value={form.salePrice}
                        onChange={handleChange("salePrice")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Currency</Form.Label>
                      <Form.Control
                        value={form.currency}
                        onChange={handleChange("currency")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stock</Form.Label>
                      <Form.Control
                        type="number"
                        value={form.stock}
                        onChange={handleChange("stock")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Stock status</Form.Label>
                      <Form.Select
                        value={form.stockStatus}
                        onChange={handleChange("stockStatus")}
                      >
                        <option value="instock">In stock</option>
                        <option value="outofstock">Out of stock</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Trạng thái</Form.Label>
                      <Form.Select
                        value={form.status}
                        onChange={handleChange("status")}
                      >
                        <option value="draft">Draft</option>
                        <option value="ready">Ready</option>
                        <option value="imported">Imported</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Button variant="primary" type="submit" disabled={saving} className="w-100">
                  <FontAwesomeIcon icon={faFloppyDisk} className="me-2" />
                  {saving ? "Đang lưu..." : "Lưu thay đổi"}
                </Button>
              </div>

              <div className="products-detail__section">
                <h6 className="mb-3">Nguồn dữ liệu</h6>
                <p className="mb-1">
                  <strong>Source:</strong> {product.source}
                </p>
                <p className="mb-1">
                  <strong>Source ID:</strong> {product.sourceId}
                </p>
                {product.sourceShop && (
                  <p className="mb-1">
                    <strong>Shop:</strong> {product.sourceShop}
                  </p>
                )}
              </div>
            </Col>
          </Row>
        </Form>
      </div>
    </main>
  );
};

export default ProductDetailPage;
